import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatINR, productImg } from "@/lib/api";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, DollarSign, Users, ShoppingCart, Tag, Upload, X, ImagePlus } from "lucide-react";
import { ImageField } from "@/components/ImageField";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const DEFAULT_CATS = ["Glitter", "Y2K", "French", "Pastel", "Foils", "3D", "Bold"];
const fullImageUrl = (src) => productImg(src);

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState(null);

  const load = useCallback(async () => {
    const [s, p, o, c, u, cnt] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/products"),
      api.get("/admin/orders"),
      api.get("/admin/coupons"),
      api.get("/admin/users"),
      api.get("/content"),
    ]);
    setStats(s.data); setProducts(p.data); setOrders(o.data); setCoupons(c.data); setUsers(u.data); setContent(cnt.data);
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const categories = (content?.categories?.length ? content.categories : DEFAULT_CATS);

  return (
    <div data-testid="admin-page" className="min-h-screen pb-20">
      <div className="max-w-[1180px] mx-auto px-6 pt-10">
        <span className="eyebrow">Admin</span>
        <h1 className="text-[clamp(2.4rem,5vw,3.5rem)] mt-3">Studio HQ</h1>

        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={DollarSign} label="Revenue" value={formatINR(stats.revenue)} testid="stat-revenue" />
            <Stat icon={ShoppingCart} label="Orders" value={stats.total_orders} testid="stat-orders" />
            <Stat icon={Package} label="Products" value={stats.total_products} testid="stat-products" />
            <Stat icon={Users} label="Customers" value={stats.total_users} testid="stat-users" />
          </div>
        )}

        <Tabs defaultValue="products" className="mt-10">
          <TabsList className="bg-white border border-[#fde0e8] rounded-full p-1.5 h-auto flex-wrap">
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-full px-6 py-2 data-[state=active]:text-white data-[state=active]:bg-[var(--bloom)]">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full px-6 py-2 data-[state=active]:text-white data-[state=active]:bg-[var(--bloom)]">Orders</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-full px-6 py-2 data-[state=active]:text-white data-[state=active]:bg-[var(--bloom)]">Customers</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-coupons" className="rounded-full px-6 py-2 data-[state=active]:text-white data-[state=active]:bg-[var(--bloom)]">Coupons</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="rounded-full px-6 py-2 data-[state=active]:text-white data-[state=active]:bg-[var(--bloom)]">Content</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-6">
            <ProductsPanel products={products} categories={categories} onChange={load} />
          </TabsContent>
          <TabsContent value="orders" className="mt-6">
            <OrdersPanel orders={orders} onChange={load} />
          </TabsContent>
          <TabsContent value="customers" className="mt-6">
            <CustomersPanel users={users} />
          </TabsContent>
          <TabsContent value="coupons" className="mt-6">
            <CouponsPanel coupons={coupons} onChange={load} />
          </TabsContent>
          <TabsContent value="content" className="mt-6">
            {content && <ContentPanel content={content} onChange={load} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="surface p-5">
      <Icon className="w-5 h-5" style={{color:"var(--bloom)"}}/>
      <div className="text-xs uppercase tracking-[0.16em] text-[var(--ink-soft)] font-bold mt-2">{label}</div>
      <div className="font-serif font-semibold text-[2rem] text-[var(--ink)] mt-1">{value}</div>
    </div>
  );
}

/* ============ PRODUCTS ============ */
function ProductsPanel({ products, categories, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const cats = categories?.length ? categories : DEFAULT_CATS;
  const blank = { name: "", slug: "", description: "", price: 49900, compare_at: null, images: [], category: cats[0], tags: [], stock: 100, featured: false };
  const [form, setForm] = useState(blank);

  const startNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p);
    setForm({ ...p, images: p.images || [], compare_at: p.compare_at || null });
    setOpen(true);
  };

  const save = async () => {
    try {
      const body = {
        ...form,
        images: form.images.filter(Boolean),
        tags: typeof form.tags === "string" ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : form.tags,
      };
      if (editing) await api.put(`/products/${editing.id}`, body);
      else await api.post("/products", body);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`); toast.success("Deleted"); onChange();
  };

  return (
    <div className="surface p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="font-serif font-semibold text-2xl text-pink-950">Products ({products.length})</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={startNew} data-testid="admin-new-product" className="btn btn-primary text-sm">
              <Plus className="w-4 h-4" /> New
            </button>
          </DialogTrigger>
          <DialogContent className="glass-strong border-white/60 rounded-3xl max-w-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-pink-950">{editing ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Fld label="Name" testid="pf-name" value={form.name} onChange={v => setForm({ ...form, name: v, slug: form.slug || v.toLowerCase().replace(/[^a-z0-9]+/g, "-") })} />
              <Fld label="Slug" testid="pf-slug" value={form.slug} onChange={v => setForm({ ...form, slug: v })} />
              <Fld label="Description" testid="pf-desc" value={form.description} onChange={v => setForm({ ...form, description: v })} textarea />
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Price (paise)" testid="pf-price" type="number" value={form.price} onChange={v => setForm({ ...form, price: parseInt(v || 0) })} />
                <Fld label="Compare-at (paise, opt)" testid="pf-compare" type="number" value={form.compare_at ?? ""} onChange={v => setForm({ ...form, compare_at: v ? parseInt(v) : null })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-slate-600 font-bold">Category</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="pf-category" className="mt-1.5 bg-white/70 border-white/60 rounded-2xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Fld label="Stock" testid="pf-stock" type="number" value={form.stock} onChange={v => setForm({ ...form, stock: parseInt(v || 0) })} />
              </div>

              {/* IMAGE UPLOADER */}
              <ImageUploader images={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} />

              <Fld label="Tags (comma-separated)" testid="pf-tags" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={v => setForm({ ...form, tags: v })} />
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} /> Featured
              </label>
            </div>
            <DialogFooter>
              <button onClick={save} data-testid="pf-save" className="btn btn-primary text-sm">Save</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.slug}`} className="bg-white rounded-2xl border border-[#fde0e8] p-3 flex gap-3">
            <img src={fullImageUrl(p.images?.[0])} alt="" className="w-20 h-20 rounded-xl object-cover bg-pink-100" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-pink-950 truncate">{p.name}</div>
              <div className="text-xs text-slate-600">{p.category} · {formatINR(p.price)} · stock {p.stock}</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => startEdit(p)} data-testid={`admin-edit-${p.slug}`} className="text-xs px-3 py-1.5 rounded-full glass hover:bg-white flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                <button onClick={() => del(p)} data-testid={`admin-del-${p.slug}`} className="text-xs px-3 py-1.5 rounded-full bg-pink-100 text-pink-700 hover:bg-pink-200 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Del</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ IMAGE UPLOADER ============ */
function ImageUploader({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const uploadFile = async (file) => {
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "video/mp4", "video/webm", "video/quicktime"];
    if (!allowed.includes(file.type)) { toast.error("Only JPG/PNG/GIF/WebP/MP4/WebM"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Max 25 MB"); return; }
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...images, data.url]);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files) => {
    Array.from(files || []).forEach(uploadFile);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeImage = (idx) => onChange(images.filter((_, i) => i !== idx));

  return (
    <div>
      <label className="text-xs uppercase tracking-[0.15em] text-slate-600 font-bold">Images</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="image-dropzone"
        className={`mt-1.5 cursor-pointer rounded-2xl border-2 border-dashed transition px-4 py-6 text-center ${
          dragging ? "border-pink-600 bg-pink-50" : "border-pink-200 bg-white/40 hover:bg-white/70"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
          data-testid="image-file-input"
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
        <ImagePlus className="w-7 h-7 mx-auto text-pink-600" />
        <div className="mt-2 text-sm font-bold text-pink-950">
          {uploading ? "Uploading…" : "Drop images or click to upload"}
        </div>
        <p className="text-xs text-slate-600 mt-0.5">JPG · PNG · WebP · GIF · MP4 · WebM · max 25MB</p>
      </div>

      {images.length > 0 && (
        <div className="mt-3 grid grid-cols-4 gap-2">
          {images.map((img, i) => (
            <div key={i} data-testid={`uploaded-img-${i}`} className="relative aspect-square rounded-xl overflow-hidden glass group">
              <img src={fullImageUrl(img)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(i)}
                data-testid={`remove-img-${i}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-pink-600 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Manual URL paste fallback */}
      <details className="mt-2">
        <summary className="text-xs text-slate-600 cursor-pointer">Or paste image URL</summary>
        <div className="flex gap-2 mt-2">
          <input
            data-testid="pf-img-url"
            placeholder="https://..."
            className="flex-1 bg-white/70 border border-white/60 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (e.target.value.trim()) {
                  onChange([...images, e.target.value.trim()]);
                  e.target.value = "";
                }
              }
            }}
          />
        </div>
      </details>
    </div>
  );
}

/* ============ ORDERS ============ */
function OrdersPanel({ orders, onChange }) {
  const STATUSES = ["processing", "shipped", "delivered", "cancelled"];
  const [openOrder, setOpenOrder] = useState(null);
  const update = async (id, val) => {
    await api.put(`/admin/orders/${id}/status`, { fulfillment_status: val });
    toast.success("Updated"); onChange();
    if (openOrder?.id === id) setOpenOrder({ ...openOrder, fulfillment_status: val });
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  return (
    <div className="surface p-6">
      <div className="font-serif font-semibold text-[1.6rem] mb-5">Orders ({orders.length})</div>
      {orders.length === 0 ? (
        <div className="text-[var(--ink-soft)] py-8 text-center">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white rounded-2xl border border-[#fde0e8] p-4 flex flex-wrap items-center gap-4">
              <button onClick={() => setOpenOrder(o)} data-testid={`open-order-${o.id}`} className="flex-1 min-w-[200px] text-left hover:opacity-70 transition">
                <div className="font-serif font-semibold text-[1.05rem]" style={{color:"var(--bloom)"}}>#{o.id.slice(-8)}</div>
                <div className="text-xs text-[var(--ink-soft)]">{o.address?.full_name || o.email} · {new Date(o.created_at).toLocaleDateString()}</div>
                <div className="text-xs text-[var(--ink-soft)]">{o.items.length} items {o.coupon_code && `· ${o.coupon_code}`}</div>
              </button>
              <div className="font-bold" style={{color:"var(--bloom)"}}>{formatINR(o.total)}</div>
              <span className={`text-[10px] uppercase font-black px-3 py-1 rounded-full ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{o.payment_status}</span>
              <Select value={o.fulfillment_status} onValueChange={(v) => update(o.id, v)}>
                <SelectTrigger data-testid={`admin-status-${o.id}`} className="w-36 bg-white border-[#fde0e8] rounded-full text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!openOrder} onOpenChange={(o) => !o && setOpenOrder(null)}>
        <DialogContent className="bg-white border-[#fde0e8] rounded-3xl max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif">Order details</DialogTitle></DialogHeader>
          {openOrder && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-serif text-[1.6rem] font-semibold" style={{color:"var(--bloom)"}}>#{openOrder.id.slice(-8)}</div>
                  <div className="text-xs text-[var(--ink-soft)]">ID: <span className="font-mono">{openOrder.id}</span></div>
                  <div className="text-xs text-[var(--ink-soft)]">Placed on {new Date(openOrder.created_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-2">
                  <span className={`text-[10px] uppercase font-black px-3 py-1 rounded-full ${openOrder.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{openOrder.payment_status}</span>
                  <span className="text-[10px] uppercase font-black px-3 py-1 rounded-full" style={{background:"var(--rose)",color:"var(--ink)"}}>{openOrder.fulfillment_status}</span>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-[var(--petal)] rounded-2xl p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{color:"var(--bloom)"}}>Customer</div>
                  <div className="mt-2 font-bold">{openOrder.address.full_name}</div>
                  <div className="text-sm text-[var(--ink-soft)]">{openOrder.email}</div>
                  <div className="text-sm text-[var(--ink-soft)]">📞 {openOrder.address.phone}</div>
                  <button onClick={() => copy(`${openOrder.address.full_name}\n${openOrder.email}\n${openOrder.address.phone}`)} className="mt-2 text-[10px] uppercase tracking-wider font-bold" style={{color:"var(--bloom)"}}>Copy contact</button>
                </div>
                <div className="bg-[var(--petal)] rounded-2xl p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{color:"var(--bloom)"}}>Shipping address</div>
                  <div className="mt-2 text-sm leading-relaxed">
                    {openOrder.address.line1}<br/>
                    {openOrder.address.line2 && <>{openOrder.address.line2}<br/></>}
                    {openOrder.address.city}, {openOrder.address.state} {openOrder.address.pincode}<br/>
                    {openOrder.address.country}
                  </div>
                  <button onClick={() => copy(`${openOrder.address.full_name}, ${openOrder.address.line1}${openOrder.address.line2?", "+openOrder.address.line2:""}, ${openOrder.address.city}, ${openOrder.address.state} ${openOrder.address.pincode}, ${openOrder.address.country}. Phone: ${openOrder.address.phone}`)} className="mt-2 text-[10px] uppercase tracking-wider font-bold" style={{color:"var(--bloom)"}}>Copy full address</button>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-3" style={{color:"var(--bloom)"}}>Items ({openOrder.items.length})</div>
                <div className="space-y-2">
                  {openOrder.items.map((i) => (
                    <div key={i.product_id} className="flex items-center gap-3 bg-[var(--petal)] rounded-2xl p-3">
                      <img src={productImg(i.image)} alt="" className="w-14 h-14 rounded-xl object-cover"/>
                      <div className="flex-1">
                        <div className="font-serif font-semibold">{i.name}</div>
                        <div className="text-xs text-[var(--ink-soft)]">Qty {i.qty} · {formatINR(i.price)} each</div>
                      </div>
                      <div className="font-bold" style={{color:"var(--bloom)"}}>{formatINR(i.price * i.qty)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[var(--petal)] rounded-2xl p-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[var(--ink-soft)]">Subtotal</span><span className="font-bold">{formatINR(openOrder.subtotal)}</span></div>
                {openOrder.discount > 0 && (<div className="flex justify-between text-green-700"><span>Discount {openOrder.coupon_code && `(${openOrder.coupon_code})`}</span><span className="font-bold">−{formatINR(openOrder.discount)}</span></div>)}
                <div className="flex justify-between"><span className="text-[var(--ink-soft)]">Shipping</span><span className="font-bold">{openOrder.shipping === 0 ? "Free" : formatINR(openOrder.shipping)}</span></div>
                <div className="flex justify-between font-serif text-[1.1rem] font-semibold pt-2 border-t border-[#fde0e8]"><span>Total</span><span>{formatINR(openOrder.total)}</span></div>
              </div>

              {(openOrder.razorpay_order_id || openOrder.razorpay_payment_id) && (
                <div className="bg-[var(--petal)] rounded-2xl p-4 text-xs">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2" style={{color:"var(--bloom)"}}>Payment</div>
                  {openOrder.razorpay_order_id && <div>Razorpay order: <span className="font-mono">{openOrder.razorpay_order_id}</span></div>}
                  {openOrder.razorpay_payment_id && <div>Payment ID: <span className="font-mono">{openOrder.razorpay_payment_id}</span></div>}
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => update(openOrder.id, s)} data-testid={`mark-${s}-${openOrder.id}`}
                    className={`text-xs px-4 py-2 rounded-full font-bold uppercase tracking-wider transition ${openOrder.fulfillment_status === s ? "text-white" : "bg-white border border-[#fde0e8] hover:border-[var(--bloom)]"}`}
                    style={openOrder.fulfillment_status === s ? { background: "linear-gradient(135deg, var(--bloom), var(--punch))" } : {}}>
                    Mark {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ CUSTOMERS ============ */
function CustomersPanel({ users }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const filtered = users.filter((u) =>
    !query ||
    (u.name || "").toLowerCase().includes(query.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(query.toLowerCase())
  );

  const openCustomer = async (u) => {
    setSelected(u);
    setLoadingOrders(true);
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/orders`);
      setOrders(data);
    } finally {
      setLoadingOrders(false);
    }
  };

  return (
    <div className="surface p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="font-serif font-semibold text-2xl text-pink-950">Customers ({users.length})</div>
        <input
          data-testid="customer-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full md:w-72 bg-white/70 border border-white/60 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-slate-600 py-10 text-center">
          <Users className="w-8 h-8 mx-auto text-pink-600 mb-2" />
          No customers yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.15em] text-slate-600">
                <th className="pb-3">Customer</th>
                <th className="pb-3">Joined</th>
                <th className="pb-3 text-center">Orders</th>
                <th className="pb-3 text-right">Spent</th>
                <th className="pb-3 text-right">Last order</th>
                <th className="pb-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id} data-testid={`customer-${u.user_id}`} className="border-t border-white/60">
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-pink-100 text-pink-700 font-bold flex items-center justify-center">
                          {(u.name?.[0] || u.email?.[0] || "?").toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-pink-950 flex items-center gap-2">
                          {u.name || "—"}
                          {u.role === "admin" && (
                            <span className="text-[9px] uppercase tracking-wider bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full font-bold">Admin</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-600">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 text-slate-600">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="py-3 text-center font-bold text-pink-950">{u.orders_count || 0}</td>
                  <td className="py-3 text-right font-bold text-pink-600">{formatINR(u.total_spent || 0)}</td>
                  <td className="py-3 text-right text-slate-600 text-xs">{u.last_order ? new Date(u.last_order).toLocaleDateString() : "—"}</td>
                  <td className="py-3 text-right">
                    <button
                      onClick={() => openCustomer(u)}
                      data-testid={`view-customer-${u.user_id}`}
                      className="text-xs px-3 py-1.5 rounded-full glass hover:bg-white text-pink-950 font-bold uppercase tracking-wider"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="glass-strong border-white/60 rounded-3xl max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-pink-950">Customer details</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                {selected.picture ? (
                  <img src={selected.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-pink-100 text-pink-700 text-2xl font-bold flex items-center justify-center">
                    {(selected.name?.[0] || selected.email?.[0] || "?").toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-serif font-semibold text-2xl text-pink-950">{selected.name || "—"}</div>
                  <div className="text-sm text-slate-700">{selected.email}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Joined {selected.created_at ? new Date(selected.created_at).toLocaleDateString() : "—"} · ID {selected.user_id}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Stat2 label="Orders" value={selected.orders_count || 0} />
                <Stat2 label="Total spent" value={formatINR(selected.total_spent || 0)} />
                <Stat2 label="Last order" value={selected.last_order ? new Date(selected.last_order).toLocaleDateString() : "—"} />
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-pink-600 font-bold mb-3">Order history</div>
                {loadingOrders ? (
                  <div className="text-slate-600">Loading…</div>
                ) : orders.length === 0 ? (
                  <div className="text-slate-600 text-sm py-4 text-center">No orders yet</div>
                ) : (
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {orders.map((o) => (
                      <div key={o.id} data-testid={`customer-order-${o.id}`} className="bg-white rounded-2xl border border-[#fde0e8] p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="font-bold text-pink-950 text-sm">#{o.id.slice(-8)}</div>
                          <div className="text-xs text-slate-600">{new Date(o.created_at).toLocaleString()}</div>
                          <span className={`text-[10px] uppercase font-black px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                            {o.payment_status}
                          </span>
                          <span className="text-[10px] uppercase font-black px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">
                            {o.fulfillment_status}
                          </span>
                          <div className="font-bold text-pink-600 text-sm">{formatINR(o.total)}</div>
                        </div>
                        <div className="mt-2 text-xs text-slate-700">
                          {o.items.map((i) => `${i.qty}× ${i.name}`).join(" · ")}
                        </div>
                        <div className="mt-1 text-[10px] text-slate-500">
                          Ships to: {o.address.full_name}, {o.address.city}, {o.address.state} {o.address.pincode}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat2({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[#fde0e8] p-3 text-center">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-600 font-bold">{label}</div>
      <div className="font-serif font-semibold text-xl text-pink-950 mt-1">{value}</div>
    </div>
  );
}

/* ============ CONTENT (CMS) ============ */
function ContentPanel({ content, onChange }) {
  const [c, setC] = useState(JSON.parse(JSON.stringify(content)));
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("hero");

  const set = (path, value) => {
    setC((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setArray = (key, arr) => setC((p) => ({ ...p, [key]: arr }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", c);
      toast.success("Saved · home page updated ✨");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset ALL homepage content to defaults? You'll lose any custom text.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/content/reset");
      setC(data);
      toast.success("Reset to defaults");
      onChange();
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: "hero",         label: "Hero" },
    { id: "nav",          label: "Navigation" },
    { id: "marquees",     label: "Marquees" },
    { id: "rail",         label: "Featured rail" },
    { id: "kids",         label: "Kids collection" },
    { id: "social_proof", label: "Social proof (UGC)" },
    { id: "editorial",    label: "Editorial" },
    { id: "bundle",       label: "Bundle band" },
    { id: "steps",        label: "How to apply" },
    { id: "collections",  label: "Collections" },
    { id: "testimonials", label: "Testimonials" },
    { id: "values",       label: "Core values" },
    { id: "story",        label: "Our story" },
    { id: "newsletter",   label: "Newsletter" },
    { id: "quiz",         label: "Nail quiz" },
    { id: "perks",        label: "Product perks" },
    { id: "categories",   label: "Categories" },
    { id: "analytics",    label: "Analytics" },
    { id: "payments",     label: "Payments" },
    { id: "brand",        label: "Brand & footer" },
  ];

  return (
    <div className="surface p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="font-serif font-semibold text-2xl text-pink-950">Homepage content</div>
          <p className="text-xs text-slate-600 mt-0.5">Edit any text/image on the home page — saved live to your store.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} data-testid="content-reset" className="text-xs px-4 py-2 rounded-full bg-pink-100 text-pink-700 hover:bg-pink-200 font-bold uppercase tracking-wider">Reset</button>
          <button onClick={save} disabled={saving} data-testid="content-save" className="bg-pink-600 hover:bg-pink-700 disabled:opacity-50 text-white px-6 py-2 rounded-full font-bold uppercase tracking-wider text-xs">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        {/* Section nav */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-[#fde0e8] p-2 flex lg:flex-col gap-1 flex-wrap">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                data-testid={`content-sec-${s.id}`}
                className={`text-xs uppercase tracking-[0.15em] font-bold px-3 py-2 rounded-xl text-left transition ${
                  section === s.id ? "bg-pink-600 text-white" : "text-pink-950 hover:bg-white/70"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Editor pane */}
        <div className="lg:col-span-9 space-y-3">
          {section === "hero" && (
            <>
              <Fld label="Badge text" testid="cnt-hero-badge" value={c.hero.badge} onChange={(v) => set("hero.badge", v)} />
              <div className="grid sm:grid-cols-3 gap-3">
                <Fld label="Headline line 1" testid="cnt-hero-h1" value={c.hero.headline_line1} onChange={(v) => set("hero.headline_line1", v)} />
                <Fld label="Italic word" testid="cnt-hero-italic" value={c.hero.headline_italic} onChange={(v) => set("hero.headline_italic", v)} />
                <Fld label="Strikethrough word" testid="cnt-hero-strike" value={c.hero.headline_strike} onChange={(v) => set("hero.headline_strike", v)} />
              </div>
              <Fld label="Subtitle" testid="cnt-hero-sub" value={c.hero.subtitle} onChange={(v) => set("hero.subtitle", v)} textarea />
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Primary CTA text" testid="cnt-hero-cta1" value={c.hero.primary_cta} onChange={(v) => set("hero.primary_cta", v)} />
                <Fld label="Secondary CTA text" testid="cnt-hero-cta2" value={c.hero.secondary_cta} onChange={(v) => set("hero.secondary_cta", v)} />
                <Fld label="Secondary CTA link" testid="cnt-hero-cta2link" value={c.hero.secondary_link} onChange={(v) => set("hero.secondary_link", v)} />
              </div>
              <ImageField
                label="Hero image"
                testid="cnt-hero-img"
                value={c.hero.image}
                onChange={(v) => set("hero.image", v)}
                aspect="tall"
              />
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="bg-white rounded-2xl border border-[#fde0e8] p-3 space-y-2">
                    <Fld label={`Stat ${n} value`} testid={`cnt-hero-stat${n}v`} value={c.hero[`stat${n}_value`]} onChange={(v) => set(`hero.stat${n}_value`, v)} />
                    <Fld label={`Stat ${n} label`} testid={`cnt-hero-stat${n}l`} value={c.hero[`stat${n}_label`]} onChange={(v) => set(`hero.stat${n}_label`, v)} />
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Review badge quote" testid="cnt-hero-rq" value={c.hero.badge_review} onChange={(v) => set("hero.badge_review", v)} />
                <Fld label="Review badge author" testid="cnt-hero-ra" value={c.hero.badge_review_author} onChange={(v) => set("hero.badge_review_author", v)} />
                <Fld label="Discount badge top" testid="cnt-hero-dt" value={c.hero.badge_discount_top} onChange={(v) => set("hero.badge_discount_top", v)} />
                <Fld label="Discount badge bottom" testid="cnt-hero-db" value={c.hero.badge_discount_bottom} onChange={(v) => set("hero.badge_discount_bottom", v)} />
              </div>
            </>
          )}

          {section === "marquees" && (
            <>
              <StringList label="Top marquee (pink bar)" items={c.marquee_top} onChange={(arr) => setArray("marquee_top", arr)} testidPrefix="cnt-mq-top" />
              <StringList label="Middle marquee (big display)" items={c.marquee_middle} onChange={(arr) => setArray("marquee_middle", arr)} testidPrefix="cnt-mq-mid" />
              <StringList label="Bottom marquee" items={c.marquee_bottom} onChange={(arr) => setArray("marquee_bottom", arr)} testidPrefix="cnt-mq-bot" />
            </>
          )}

          {section === "rail" && (
            <>
              <Fld label="Eyebrow" testid="cnt-rail-eye" value={c.rail.eyebrow} onChange={(v) => set("rail.eyebrow", v)} />
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Headline part 1" testid="cnt-rail-h1" value={c.rail.headline_part1} onChange={(v) => set("rail.headline_part1", v)} />
                <Fld label="Italic word" testid="cnt-rail-h2" value={c.rail.headline_italic} onChange={(v) => set("rail.headline_italic", v)} />
                <Fld label="Tab label (Drop)" testid="cnt-rail-t1" value={c.rail.tab_drop} onChange={(v) => set("rail.tab_drop", v)} />
                <Fld label="Tab label (Favs)" testid="cnt-rail-t2" value={c.rail.tab_favs} onChange={(v) => set("rail.tab_favs", v)} />
              </div>
            </>
          )}

          {section === "kids" && (
            <>
              <p className="text-xs text-slate-600 mb-3">Kids-collection band on the home page. Products with <b>Category = Kids</b> automatically appear here. Uncheck &ldquo;Show on home&rdquo; to hide the whole section.</p>
              <label className="flex items-center gap-3 text-sm cursor-pointer mb-3">
                <input
                  type="checkbox"
                  data-testid="cnt-kids-enabled"
                  checked={!!(c.kids || {}).enabled}
                  onChange={(e) => set("kids.enabled", e.target.checked)}
                  className="w-4 h-4 accent-pink-500"
                />
                <span className="font-bold uppercase tracking-[0.15em] text-xs text-slate-700">Show Kids collection on home</span>
              </label>
              <Fld label="Eyebrow"       testid="cnt-kids-eye" value={(c.kids || {}).eyebrow || ""}       onChange={(v) => set("kids.eyebrow", v)} />
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Headline part 1" testid="cnt-kids-h1"  value={(c.kids || {}).headline_part1 || ""} onChange={(v) => set("kids.headline_part1", v)} />
                <Fld label="Italic word"     testid="cnt-kids-h2"  value={(c.kids || {}).headline_italic || ""} onChange={(v) => set("kids.headline_italic", v)} />
              </div>
              <Fld label="Subtitle" testid="cnt-kids-sub" value={(c.kids || {}).subtitle || ""} onChange={(v) => set("kids.subtitle", v)} />
            </>
          )}

          {section === "social_proof" && (
            <>
              <p className="text-xs text-slate-600 mb-3">Instagram-style UGC grid on the home page. Add images (JPG/PNG) or videos (MP4/WebM). Videos autoplay muted. Leave the URL blank to hide an item.</p>
              <label className="flex items-center gap-3 text-sm cursor-pointer mb-3">
                <input
                  type="checkbox"
                  data-testid="cnt-sp-enabled"
                  checked={!!(c.social_proof || {}).enabled}
                  onChange={(e) => set("social_proof.enabled", e.target.checked)}
                  className="w-4 h-4 accent-pink-500"
                />
                <span className="font-bold uppercase tracking-[0.15em] text-xs text-slate-700">Show Social Proof section on home</span>
              </label>
              <Fld label="Eyebrow"       testid="cnt-sp-eye" value={(c.social_proof || {}).eyebrow || ""}       onChange={(v) => set("social_proof.eyebrow", v)} />
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Headline part 1" testid="cnt-sp-h1"  value={(c.social_proof || {}).headline_part1 || ""} onChange={(v) => set("social_proof.headline_part1", v)} />
                <Fld label="Italic word"     testid="cnt-sp-h2"  value={(c.social_proof || {}).headline_italic || ""} onChange={(v) => set("social_proof.headline_italic", v)} />
              </div>
              <Fld label="Subtitle" testid="cnt-sp-sub" value={(c.social_proof || {}).subtitle || ""} onChange={(v) => set("social_proof.subtitle", v)} />
              <div className="mt-2">
                <ItemList
                  items={(c.social_proof || {}).items || []}
                  onChange={(arr) => set("social_proof.items", arr)}
                  blank={{ type: "image", url: "", author: "" }}
                  fields={[
                    { key: "type",   label: "Type (image or video)" },
                    { key: "url",    label: "Media URL (upload below)", image: true },
                    { key: "author", label: "Handle (e.g. @jane_nailedit)" },
                  ]}
                  testidPrefix="cnt-sp"
                />
              </div>
            </>
          )}

          {section === "editorial" && (
            <>
              <Fld label="Eyebrow" testid="cnt-ed-eye" value={c.editorial.eyebrow} onChange={(v) => set("editorial.eyebrow", v)} />
              <Fld label="Title (use Enter for line break)" testid="cnt-ed-title" value={c.editorial.title} onChange={(v) => set("editorial.title", v)} textarea />
              <Fld label="CTA button text" testid="cnt-ed-cta" value={c.editorial.cta} onChange={(v) => set("editorial.cta", v)} />
            </>
          )}

          {section === "collections" && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Eyebrow" testid="cnt-col-eye" value={c.collections_eyebrow} onChange={(v) => set("collections_eyebrow", v)} />
                <Fld label="Headline" testid="cnt-col-head" value={c.collections_headline} onChange={(v) => set("collections_headline", v)} />
              </div>
              <ItemList
                items={c.collections}
                onChange={(arr) => setArray("collections", arr)}
                blank={{ label: "", cat: "Glitter", img: "" }}
                fields={[
                  { key: "label", label: "Label (shown on tile)" },
                  { key: "cat",   label: "Category to filter (e.g. Glitter, Y2K, Pastel)" },
                  { key: "img",   label: "Tile image", image: true, aspect: "wide" },
                ]}
                testidPrefix="cnt-col"
              />
            </>
          )}

          {section === "testimonials" && (
            <>
              <div className="grid sm:grid-cols-3 gap-3">
                <Fld label="Eyebrow" testid="cnt-test-eye" value={c.testimonials_eyebrow} onChange={(v) => set("testimonials_eyebrow", v)} />
                <Fld label="Headline" testid="cnt-test-head" value={c.testimonials_headline} onChange={(v) => set("testimonials_headline", v)} />
                <Fld label="Subhead" testid="cnt-test-sub" value={c.testimonials_subhead} onChange={(v) => set("testimonials_subhead", v)} />
              </div>
              <ItemList
                items={c.testimonials}
                onChange={(arr) => setArray("testimonials", arr)}
                blank={{ name: "", city: "", quote: "", img: "" }}
                fields={[
                  { key: "name",  label: "Name" },
                  { key: "city",  label: "City" },
                  { key: "quote", label: "Quote", textarea: true },
                  { key: "img",   label: "Photo", image: true, aspect: "tall" },
                ]}
                testidPrefix="cnt-test"
              />
            </>
          )}

          {section === "values" && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Eyebrow" testid="cnt-val-eye" value={c.values_eyebrow} onChange={(v) => set("values_eyebrow", v)} />
                <Fld label="Headline" testid="cnt-val-head" value={c.values_headline} onChange={(v) => set("values_headline", v)} />
              </div>
              <ItemList
                items={c.values}
                onChange={(arr) => setArray("values", arr)}
                blank={{ icon: "sparkles", title: "", desc: "" }}
                fields={[
                  { key: "icon",  label: "Icon (sparkles | heart | zap | shield)" },
                  { key: "title", label: "Title" },
                  { key: "desc",  label: "Description", textarea: true },
                ]}
                testidPrefix="cnt-val"
              />
            </>
          )}

          {section === "story" && (
            <>
              <Fld label="Eyebrow" testid="cnt-st-eye" value={c.story.eyebrow} onChange={(v) => set("story.eyebrow", v)} />
              <div className="grid sm:grid-cols-3 gap-3">
                <Fld label="Headline line 1" testid="cnt-st-h1" value={c.story.headline_line1} onChange={(v) => set("story.headline_line1", v)} />
                <Fld label="Italic phrase" testid="cnt-st-it" value={c.story.headline_italic} onChange={(v) => set("story.headline_italic", v)} />
                <Fld label="Headline tail" testid="cnt-st-h2" value={c.story.headline_line2} onChange={(v) => set("story.headline_line2", v)} />
              </div>
              <Fld label="Body paragraph 1" testid="cnt-st-b1" value={c.story.body1} onChange={(v) => set("story.body1", v)} textarea />
              <Fld label="Body paragraph 2" testid="cnt-st-b2" value={c.story.body2} onChange={(v) => set("story.body2", v)} textarea />
              <Fld label="Big quote (left panel)" testid="cnt-st-quote" value={c.story_quote} onChange={(v) => set("story_quote", v)} textarea />
              <Fld label="Signature line" testid="cnt-st-sign" value={c.story_sign} onChange={(v) => set("story_sign", v)} />
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="CTA text" testid="cnt-st-cta" value={c.story.cta} onChange={(v) => set("story.cta", v)} />
              </div>
              <ImageField
                label="Story image"
                testid="cnt-st-img"
                value={c.story.image}
                onChange={(v) => set("story.image", v)}
                aspect="tall"
              />
            </>
          )}

          {section === "quiz" && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Eyebrow" testid="cnt-q-eye" value={c.quiz.eyebrow} onChange={(v) => set("quiz.eyebrow", v)} />
                <Fld label="Result eyebrow" testid="cnt-q-re" value={c.quiz.result_eyebrow} onChange={(v) => set("quiz.result_eyebrow", v)} />
                <Fld label="Headline line 1" testid="cnt-q-h1" value={c.quiz.headline_line1} onChange={(v) => set("quiz.headline_line1", v)} />
                <Fld label="Headline line 2" testid="cnt-q-h2" value={c.quiz.headline_line2} onChange={(v) => set("quiz.headline_line2", v)} />
              </div>
              <Fld label="Subtitle" testid="cnt-q-sub" value={c.quiz.subtitle} onChange={(v) => set("quiz.subtitle", v)} textarea />
              <ItemList
                items={c.quiz.options}
                onChange={(arr) => set("quiz.options", arr)}
                blank={{ id: "", label: "", cat: "Glitter", emoji: "✨", desc: "" }}
                fields={[
                  { key: "id",    label: "ID (unique, lowercase)" },
                  { key: "label", label: "Button label" },
                  { key: "emoji", label: "Emoji" },
                  { key: "cat",   label: "Recommended category" },
                  { key: "desc",  label: "Vibe description shown on result", textarea: true },
                ]}
                testidPrefix="cnt-q-opt"
              />
            </>
          )}

          {section === "brand" && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Brand name (left half, regular)" testid="cnt-br-l" value={c.brand.name_left} onChange={(v) => set("brand.name_left", v)} />
                <Fld label="Brand name (right half, pink)" testid="cnt-br-r" value={c.brand.name_right} onChange={(v) => set("brand.name_right", v)} />
              </div>
              <Fld label="Footer tagline" testid="cnt-br-tg" value={c.brand.footer_tagline} onChange={(v) => set("brand.footer_tagline", v)} textarea />
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Footer email" testid="cnt-br-em" value={c.brand.footer_email} onChange={(v) => set("brand.footer_email", v)} />
                <Fld label="Footer social handle" testid="cnt-br-so" value={c.brand.footer_social} onChange={(v) => set("brand.footer_social", v)} />
              </div>
              <Fld label="Copyright line" testid="cnt-br-cp" value={c.brand.footer_copyright} onChange={(v) => set("brand.footer_copyright", v)} />
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Footer column 1 title" testid="cnt-br-ft-shop-t" value={(c.footer_shop || {}).title} onChange={(v) => set("footer_shop.title", v)} />
                <Fld label="Footer column 2 title" testid="cnt-br-ft-hello-t" value={(c.footer_hello || {}).title} onChange={(v) => set("footer_hello.title", v)} />
              </div>
              <ItemList
                items={c.footer_shop?.links || []}
                onChange={(arr) => set("footer_shop.links", arr)}
                blank={{ label: "", to: "/shop" }}
                fields={[{ key: "label", label: "Link label" }, { key: "to", label: "URL" }]}
                testidPrefix="cnt-br-ft-shop"
              />
              <Fld label="Footer contact line 1" testid="cnt-br-ft-h1" value={(c.footer_hello || {}).line1} onChange={(v) => set("footer_hello.line1", v)} />
              <Fld label="Footer contact line 2" testid="cnt-br-ft-h2" value={(c.footer_hello || {}).line2} onChange={(v) => set("footer_hello.line2", v)} />
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-slate-600 font-bold mb-2">Social links (footer)</div>
                <p className="text-xs text-slate-500 mb-2">Add each platform with its full URL — e.g. label &ldquo;Instagram&rdquo;, URL &ldquo;https://instagram.com/naileditbeauty&rdquo;. Buttons open in a new tab.</p>
                <ItemList
                  items={(c.footer_socials || []).map((s) => (typeof s === "string" ? { label: s, url: "" } : s))}
                  onChange={(arr) => setArray("footer_socials", arr)}
                  blank={{ label: "Instagram", url: "" }}
                  fields={[
                    { key: "label", label: "Label (e.g. Instagram)" },
                    { key: "url", label: "Profile URL (https://...)" },
                  ]}
                  testidPrefix="cnt-br-ft-sock"
                />
              </div>
            </>
          )}

          {section === "nav" && (
            <ItemList
              items={c.nav || []}
              onChange={(arr) => setArray("nav", arr)}
              blank={{ label: "", to: "/" }}
              fields={[{ key: "label", label: "Link label" }, { key: "to", label: "URL (e.g. /shop or /shop?category=Y2K)" }]}
              testidPrefix="cnt-nav"
            />
          )}

          {section === "bundle" && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Eyebrow" testid="cnt-bd-eye" value={(c.bundle || {}).eyebrow} onChange={(v) => set("bundle.eyebrow", v)} />
                <Fld label="Headline" testid="cnt-bd-head" value={(c.bundle || {}).headline} onChange={(v) => set("bundle.headline", v)} />
              </div>
              <Fld label="Subtitle" testid="cnt-bd-sub" value={(c.bundle || {}).subtitle} onChange={(v) => set("bundle.subtitle", v)} textarea />
              <Fld label="CTA button" testid="cnt-bd-cta" value={(c.bundle || {}).cta} onChange={(v) => set("bundle.cta", v)} />
              <ItemList
                items={(c.bundle || {}).tiers || []}
                onChange={(arr) => set("bundle.tiers", arr)}
                blank={{ n: "2", s: "save 10%" }}
                fields={[{ key: "n", label: "Number shown" }, { key: "s", label: "Sub-label" }]}
                testidPrefix="cnt-bd-t"
              />
            </>
          )}

          {section === "steps" && (
            <>
              <Fld label="Eyebrow" testid="cnt-st-sec-eye" value={(c.steps_section || {}).eyebrow} onChange={(v) => set("steps_section.eyebrow", v)} />
              <Fld label="Headline" testid="cnt-st-sec-h" value={(c.steps_section || {}).headline} onChange={(v) => set("steps_section.headline", v)} />
              <Fld label="Subtitle" testid="cnt-st-sec-sub" value={(c.steps_section || {}).subtitle} onChange={(v) => set("steps_section.subtitle", v)} textarea />
              <ItemList
                items={c.steps || []}
                onChange={(arr) => setArray("steps", arr)}
                blank={{ n: "05", icon: "✨", title: "", desc: "" }}
                fields={[
                  { key: "n",     label: "Step number (e.g. 01)" },
                  { key: "icon",  label: "Emoji icon" },
                  { key: "title", label: "Title" },
                  { key: "desc",  label: "Description", textarea: true },
                ]}
                testidPrefix="cnt-step"
              />
            </>
          )}

          {section === "newsletter" && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Eyebrow" testid="cnt-nw-eye" value={(c.newsletter || {}).eyebrow} onChange={(v) => set("newsletter.eyebrow", v)} />
                <Fld label="Headline" testid="cnt-nw-head" value={(c.newsletter || {}).headline} onChange={(v) => set("newsletter.headline", v)} />
              </div>
              <Fld label="Subtitle" testid="cnt-nw-sub" value={(c.newsletter || {}).subtitle} onChange={(v) => set("newsletter.subtitle", v)} textarea />
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Email placeholder" testid="cnt-nw-ph" value={(c.newsletter || {}).placeholder} onChange={(v) => set("newsletter.placeholder", v)} />
                <Fld label="Button text" testid="cnt-nw-cta" value={(c.newsletter || {}).cta} onChange={(v) => set("newsletter.cta", v)} />
              </div>
            </>
          )}

          {section === "perks" && (
            <ItemList
              items={c.perks || []}
              onChange={(arr) => setArray("perks", arr)}
              blank={{ icon: "✨", title: "" }}
              fields={[
                { key: "icon",  label: "Emoji" },
                { key: "title", label: "Perk text" },
              ]}
              testidPrefix="cnt-perk"
            />
          )}

          {section === "categories" && (
            <div>
              <div className="mb-3">
                <div className="font-serif font-semibold text-lg text-pink-950">Product categories</div>
                <p className="text-xs text-slate-600 mt-1">These power the Shop filter chips, the product form dropdown and any nav links that reference them. Add or remove as needed — existing products keep their category value even if you rename it here.</p>
              </div>
              <StringList
                label="Category names"
                items={c.categories || []}
                onChange={(arr) => setArray("categories", arr)}
                testidPrefix="cnt-cat"
              />
            </div>
          )}

          {section === "analytics" && (
            <div className="bg-white rounded-2xl border border-[#fde0e8] p-5 space-y-6">
              <div>
                <div className="font-serif font-semibold text-lg text-pink-950">Google Analytics (GA4)</div>
                <p className="text-xs text-slate-600 mt-1">Paste your GA4 Measurement ID here. It loads on every page and auto-tracks route changes. Leave empty to disable.</p>
                <p className="text-xs text-slate-500 mt-1">Find it in Google Analytics → Admin → Data Streams → your web stream → &ldquo;Measurement ID&rdquo; (starts with <code className="bg-pink-50 px-1 rounded">G-</code>).</p>
                <Fld
                  label="GA4 Measurement ID"
                  testid="cnt-analytics-ga4"
                  value={(c.analytics || {}).ga4_id || ""}
                  onChange={(v) => set("analytics.ga4_id", v.trim())}
                  placeholder="G-XXXXXXXXXX"
                />
              </div>

              <div className="border-t border-[#fde0e8] pt-5">
                <div className="font-serif font-semibold text-lg text-pink-950">Microsoft Clarity</div>
                <p className="text-xs text-slate-600 mt-1">Session recordings, heatmaps, rage-click detection — everything GA4 doesn&apos;t show. Leave empty to disable.</p>
                <p className="text-xs text-slate-500 mt-1">Find it at <a href="https://clarity.microsoft.com/projects" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">clarity.microsoft.com/projects</a> → your project → Settings → Setup → the 10-character alphanumeric ID at the top-left.</p>
                <Fld
                  label="Clarity Project ID"
                  testid="cnt-analytics-clarity"
                  value={(c.analytics || {}).clarity_id || ""}
                  onChange={(v) => set("analytics.clarity_id", v.trim())}
                  placeholder="k8q2xyz9pl"
                />
              </div>

              <div className="border-t border-[#fde0e8] pt-5">
                <div className="font-serif font-semibold text-lg text-pink-950">Meta / Facebook Pixel</div>
                <p className="text-xs text-slate-600 mt-1">Powers Instagram &amp; Facebook ad retargeting + conversion tracking. Fires PageView on every route. Leave empty to disable.</p>
                <p className="text-xs text-slate-500 mt-1">Find it at <a href="https://business.facebook.com/events_manager" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">business.facebook.com/events_manager</a> → your Pixel → Settings — the 15-16 digit numeric ID.</p>
                <Fld
                  label="Pixel ID"
                  testid="cnt-analytics-pixel"
                  value={(c.analytics || {}).meta_pixel_id || ""}
                  onChange={(v) => set("analytics.meta_pixel_id", v.trim())}
                  placeholder="1693719428504594"
                />
              </div>
            </div>
          )}

          {section === "payments" && (
            <div className="bg-white rounded-2xl border border-[#fde0e8] p-5 space-y-4">
              <div>
                <div className="font-serif font-semibold text-lg text-pink-950">Cash on Delivery</div>
                <p className="text-xs text-slate-600 mt-1">Show or hide COD as a checkout option, and set the handling fee added to the order total. Phone OTP verification is always required for COD orders.</p>
              </div>
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="cnt-payments-cod-enabled"
                  checked={!!(c.payments || {}).cod_enabled}
                  onChange={(e) => set("payments.cod_enabled", e.target.checked)}
                  className="w-4 h-4 accent-pink-500"
                />
                <span className="font-bold uppercase tracking-[0.15em] text-xs text-slate-700">Enable Cash on Delivery</span>
              </label>
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="cnt-payments-cod-otp"
                  checked={!!(c.payments || {}).cod_otp_required}
                  onChange={(e) => set("payments.cod_otp_required", e.target.checked)}
                  className="w-4 h-4 accent-pink-500"
                />
                <span className="font-bold uppercase tracking-[0.15em] text-xs text-slate-700">Require mobile OTP for COD orders</span>
              </label>
              <p className="text-[11px] text-slate-500 -mt-2">Keep this <b>off</b> until your MSG91 DLT template is approved. Turn on to add SMS verification before a customer can place a COD order — reduces fake orders / RTO.</p>
              <div>
                <span className="text-xs uppercase tracking-[0.15em] text-slate-600 font-bold">COD handling fee (paise)</span>
                <input
                  data-testid="cnt-payments-cod-fee"
                  type="number"
                  min={0}
                  value={(c.payments || {}).cod_fee ?? 5000}
                  onChange={(e) => set("payments.cod_fee", parseInt(e.target.value || "0", 10))}
                  className="mt-1 w-full bg-white border border-pink-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-pink-500"
                />
                <p className="text-[11px] text-slate-500 mt-1">In paise. e.g. <b>5000</b> = ₹50. This fee always applies (even if shipping is free).</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StringList({ label, items, onChange, testidPrefix }) {
  const arr = items || [];
  return (
    <div className="bg-white rounded-2xl border border-[#fde0e8] p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.15em] text-slate-600 font-bold">{label}</span>
        <button onClick={() => onChange([...arr, ""])} data-testid={`${testidPrefix}-add`} className="text-xs bg-pink-600 hover:bg-pink-700 text-white px-3 py-1.5 rounded-full font-bold uppercase tracking-wider flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="space-y-2">
        {arr.map((s, i) => (
          <div key={i} className="flex gap-2">
            <input
              data-testid={`${testidPrefix}-${i}`}
              value={s}
              onChange={(e) => onChange(arr.map((v, idx) => (idx === i ? e.target.value : v)))}
              className="flex-1 bg-white/70 border border-white/60 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
            />
            <button onClick={() => onChange(arr.filter((_, idx) => idx !== i))} data-testid={`${testidPrefix}-del-${i}`} className="w-9 h-9 rounded-full bg-pink-100 text-pink-700 hover:bg-pink-200 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {arr.length === 0 && <div className="text-xs text-slate-500 italic">No items. Click Add to create one.</div>}
      </div>
    </div>
  );
}

function ItemList({ items, onChange, blank, fields, testidPrefix }) {
  const arr = items || [];
  const update = (idx, key, val) => onChange(arr.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => onChange([...arr, { ...blank }])} data-testid={`${testidPrefix}-add`} className="text-xs bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-full font-bold uppercase tracking-wider flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add item
        </button>
      </div>
      {arr.map((it, i) => (
        <div key={i} className="bg-white rounded-2xl border border-[#fde0e8] p-4 relative">
          <button onClick={() => onChange(arr.filter((_, idx) => idx !== i))} data-testid={`${testidPrefix}-del-${i}`} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-pink-100 text-pink-700 hover:bg-pink-200 flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
          <div className="text-[10px] uppercase tracking-[0.18em] text-pink-600 font-bold mb-3">#{i + 1}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) =>
              f.image ? (
                <div key={f.key} className="sm:col-span-2">
                  <ImageField
                    label={f.label}
                    testid={`${testidPrefix}-${i}-${f.key}`}
                    value={it[f.key] || ""}
                    onChange={(v) => update(i, f.key, v)}
                    aspect={f.aspect || "square"}
                  />
                </div>
              ) : (
                <Fld
                  key={f.key}
                  label={f.label}
                  testid={`${testidPrefix}-${i}-${f.key}`}
                  value={it[f.key] || ""}
                  onChange={(v) => update(i, f.key, v)}
                  textarea={f.textarea}
                />
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============ COUPONS ============ */
function CouponsPanel({ coupons, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { code: "", discount_type: "percent", value: 10, min_subtotal: 0, max_discount: null, usage_limit: null, expires_at: "", active: true };
  const [form, setForm] = useState(blank);

  const startNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const startEdit = (c) => {
    setEditing(c);
    setForm({
      ...c,
      max_discount: c.max_discount ?? null,
      usage_limit: c.usage_limit ?? null,
      expires_at: c.expires_at || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const body = {
        ...form,
        code: form.code.toUpperCase().trim(),
        value: parseInt(form.value || 0),
        min_subtotal: parseInt(form.min_subtotal || 0),
        max_discount: form.max_discount ? parseInt(form.max_discount) : null,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
        expires_at: form.expires_at || null,
      };
      if (editing) await api.put(`/admin/coupons/${editing.id}`, body);
      else await api.post("/admin/coupons", body);
      toast.success(editing ? "Coupon updated" : "Coupon created");
      setOpen(false); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"?`)) return;
    await api.delete(`/admin/coupons/${c.id}`); toast.success("Deleted"); onChange();
  };

  return (
    <div className="surface p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="font-serif font-semibold text-2xl text-pink-950">Coupons ({coupons.length})</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={startNew} data-testid="admin-new-coupon" className="btn btn-primary text-sm">
              <Plus className="w-4 h-4" /> New coupon
            </button>
          </DialogTrigger>
          <DialogContent className="glass-strong border-white/60 rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-pink-950">{editing ? "Edit coupon" : "New coupon"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Fld label="Code (e.g. SUMMER20)" testid="cf-code" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-slate-600 font-bold">Type</label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger data-testid="cf-type" className="mt-1.5 bg-white/70 border-white/60 rounded-2xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percent (%)</SelectItem>
                      <SelectItem value="flat">Flat (paise)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Fld label={form.discount_type === "percent" ? "Percent (1-100)" : "Flat amount (paise)"} testid="cf-value" type="number" value={form.value} onChange={(v) => setForm({ ...form, value: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Min subtotal (paise)" testid="cf-min" type="number" value={form.min_subtotal} onChange={(v) => setForm({ ...form, min_subtotal: v })} />
                <Fld label="Max discount (paise, opt)" testid="cf-max" type="number" value={form.max_discount ?? ""} onChange={(v) => setForm({ ...form, max_discount: v || null })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Usage limit (opt)" testid="cf-limit" type="number" value={form.usage_limit ?? ""} onChange={(v) => setForm({ ...form, usage_limit: v || null })} />
                <Fld label="Expires at (ISO date, opt)" testid="cf-exp" value={form.expires_at} onChange={(v) => setForm({ ...form, expires_at: v })} placeholder="2026-12-31T23:59:59" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="cf-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
              </label>
              <p className="text-xs text-slate-600">Tip: 1 INR = 100 paise. ₹100 = 10000 paise.</p>
            </div>
            <DialogFooter>
              <button onClick={save} data-testid="cf-save" className="btn btn-primary text-sm">Save</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {coupons.length === 0 ? (
        <div className="text-slate-600 py-10 text-center">
          <Tag className="w-8 h-8 mx-auto text-pink-600 mb-2" />
          No coupons yet. Create your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {coupons.map((c) => (
            <div key={c.id} data-testid={`admin-coupon-${c.code}`} className="bg-white rounded-2xl border border-[#fde0e8] p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-serif font-semibold text-xl text-pink-950 tracking-wide">{c.code}</span>
                    {!c.active && <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-bold">Inactive</span>}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {c.discount_type === "percent" ? `${c.value}% off` : `${formatINR(c.value)} off`}
                    {c.min_subtotal > 0 && ` · min ${formatINR(c.min_subtotal)}`}
                    {c.max_discount && ` · cap ${formatINR(c.max_discount)}`}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Used {c.times_used || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => startEdit(c)} data-testid={`edit-coupon-${c.code}`} className="text-xs px-3 py-1.5 rounded-full glass hover:bg-white flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                  <button onClick={() => del(c)} data-testid={`del-coupon-${c.code}`} className="text-xs px-3 py-1.5 rounded-full bg-pink-100 text-pink-700 hover:bg-pink-200 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Del</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Fld({ label, testid, value, onChange, type = "text", textarea, placeholder }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-slate-600 font-bold">{label}</span>
      <Tag data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1.5 w-full bg-white/70 border border-white/60 rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400" rows={3} />
    </label>
  );
}
